import {
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  TextField,
  OutlinedInput,
  Select,
  MenuItem,
  FormControlLabel,
} from "@mui/material";
import React, { useContext, useEffect, useState, useMemo } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useLocation } from "react-router-dom";
import { Box } from "@mui/system";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { GET_PACKAGE_DETAILS } from "../graphql/queries";
import { UPDATE_PACKAGE } from "../graphql/mutations";
import { DateTimePicker } from "@mui/x-date-pickers-pro";
import SaveIcon from "@mui/icons-material/Save";
import { useSnackbar } from "notistack";
import { AuthenticatedContext } from "../App";
import { useHistory } from "react-router-dom";

const PackageUpdateDetails = ({ packageId, packageType, setPackage }) => {
  const { userRole, getRole } = useContext(AuthenticatedContext);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [elPackage, setElPackage] = useState({});
  const schema = yup
    .object({
      packageName: yup.string(),
      courseId: yup.number().nullable(),
      isActive: yup.boolean(),
      easyCourses: yup.string(),
      hasDiagnostic: yup.boolean(),
      isPost: yup.boolean(),
      programId: yup.number().nullable(),
    })
    .required();
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      packageName: "",
      courseId: 0,
      isActive: false,
      easyCourses: "",
      hasDiagnostic: false,
      isPost: false,
      programId: 0,
    },
  });

  const location = useLocation();

  const [getPackageDetails] = useLazyQuery(GET_PACKAGE_DETAILS, {
    onCompleted: (returnPackageDetails) => {
      setValue("packageName", returnPackageDetails.getPackageDetails.name);
      setValue("courseId", returnPackageDetails.getPackageDetails.courseId);
      setValue(
        "isActive",
        returnPackageDetails.getPackageDetails.isActive || false
      );
      setValue(
        "hasDiagnostic",
        returnPackageDetails.getPackageDetails.hasDiagnostic || false
      );
      setValue(
        "isPost",
        returnPackageDetails.getPackageDetails.isPost || false
      );
      setValue("programId", returnPackageDetails.getPackageDetails.programId);
      setElPackage(returnPackageDetails.getPackageDetails);
      if (packageType === "Program") setPackage(returnPackageDetails.getPackageDetails);
    },
    fetchPolicy: "no-cache",
  });

  const [updatePackage] = useMutation(UPDATE_PACKAGE, {
    onError: (error) => {
      enqueueSnackbar(`A ${packageType} with the same name already exists`, {
        variant: "error",
      });
    },
    onCompleted: ({ updatePackage }) => {
      if (updatePackage) {
        enqueueSnackbar(`${packageType} successfully updated`, {
          variant: "success",
        });
      } else {
        enqueueSnackbar(
          `There was a problem with updating the ${packageType}, please try again.`,
          {
            variant: "warning",
          }
        );
      }
    },
    fetchPolicy: "network-only",
  });

  const re = useMemo(() => new RegExp("coursemanagement"), []);

  useEffect(() => {
    if (!userRole) getRole();
    if (re.test(location.pathname)) {
      getPackageDetails({
        variables: { package_id: packageId, package_type: "Course" },
      });
    } else {
      getPackageDetails({
        variables: { package_id: packageId, package_type: "Program" },
      });
    }
  }, [location.pathname, packageId, re]);

  const onSubmit = ({
    packageName,
    // availableFrom,
    // availableTo,
    courseId,
    isActive,
    hasDiagnostic,
    isPost,
    programId
  }) => {
    updatePackage({
      variables: {
        ...(re.test(location.pathname)
          ? { package_type: "Course" }
          : { package_type: "Program" }),
        name: packageName,
        package_id: packageId,
        // available_from: availableFrom,
        // available_to: availableTo,
        course_id: courseId,
        is_active: isActive,
        has_diagnostic: hasDiagnostic,
        is_post: isPost,
        program_id: programId
      },
    });
  };

  const onInvalid = (errors) => console.error(errors);

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit(onSubmit, onInvalid)}
    >
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <InputLabel>Name</InputLabel>
          <Controller
            name="packageName"
            control={control}
            render={({ field }) => (
              <FormControl
                variant="outlined"
                fullWidth
                required
                error={errors.packageName ? true : false}
                disabled={!["Admin", "Education Consultant"].includes(userRole)}
              >
                <OutlinedInput {...field} />
                <FormHelperText sx={{ color: "primary.main" }}>
                  {errors.packageName?.message}
                </FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={4}>
          {!re.test(location.pathname) ? watch("isPost") ? (
            <>
              <InputLabel>Program</InputLabel>
              <Controller
                name="programId"
                control={control}
                render={({ field }) => (
                  <FormControl
                    variant="outlined"
                    error={errors.programId ? true : false}
                    fullWidth
                  >
                    { !!(history.location.state?.data.programs &&
                      history.location.state.data.programs.split(";").length > 1) && (
                        <Select
                          {...field}
                          defaultValue={control.programId}
                          autoWidth
                          disabled={
                            !["Admin", "Education Consultant"].includes(userRole)
                          }
                        >
                          { history.location.state.data.programs
                              .split(";")
                              .map((p) => JSON.parse(p))
                              .filter((p) => Number(p.id) !== packageId)
                              .map(({ id, name }) => (
                                <MenuItem value={Number(id)} key={Number(id)}>
                                  {name}
                                </MenuItem>
                              ))}
                        </Select>
                      )
                    }
                    <FormHelperText sx={{ color: "primary.main" }}>
                      {errors.programId?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </>
          ) : (
            <>
              <InputLabel>Course</InputLabel>
              <Controller
                name="courseId"
                control={control}
                render={({ field }) => (
                  <FormControl
                    variant="outlined"
                    error={errors.courseId ? true : false}
                    fullWidth
                  >
                    { !!(history.location.state?.data.courses &&
                      history.location.state.data.courses.split(";").length > 1) && (<Select
                      {...field}
                      defaultValue={control.courseId}
                      autoWidth
                      disabled={
                        !["Admin", "Education Consultant"].includes(userRole)
                      }
                    >
                      {history.location.state?.data.courses &&
                        history.location.state.data.courses
                          .split(";")
                          .map((c) => JSON.parse(c))
                          .map(({ id, name }) => (
                            <MenuItem value={Number(id)} key={Number(id)}>
                              {name}
                            </MenuItem>
                          ))}
                    </Select>)}
                    <FormHelperText sx={{ color: "primary.main" }}>
                      {errors.courseId?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </>
          ) : <></>}
        </Grid>
        <Grid item xs={4}>
        {!re.test(location.pathname) && (
          <Grid container alignItems="center">
            <Controller
              name="isPost"
              control={control}
              error={errors.isPost ? true : false}
              render={({ field }) => (
                <FormControlLabel
                  sx={{ pt: "5%" }}
                  control={<Checkbox {...field} checked={field.value} />}
                  disabled={
                    !["Admin", "Education Consultant"].includes(userRole)
                  }
                  label="Post-Assessment Program?"
                />
              )}
            />
          </Grid>
        )}
        </Grid>
        {["Admin", "Education Consultant"].includes(userRole) && (
          <>
            {/* <Grid item xs={4} /> */}
            <Grid item xs={4}>
              <InputLabel>Created By</InputLabel>
              <TextField
                disabled
                value={`${elPackage.CreatedByStaff?.givenname} ${elPackage.CreatedByStaff?.surname}`}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <InputLabel>Created At</InputLabel>
              <FormControl fullWidth>
                <DateTimePicker
                  inputFormat="dd/MM/yyyy hh:mm"
                  value={elPackage.createdAt}
                  onChange={(event) => {}}
                  renderInput={(params) => <TextField {...params} />}
                  disabled
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4} />
            <Grid item xs={4}>
              <InputLabel>Updated By</InputLabel>
              <TextField
                disabled
                value={`${elPackage.UpdatedByStaff?.givenname} ${elPackage.UpdatedByStaff?.surname}`}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <InputLabel>Updated At</InputLabel>
              <FormControl fullWidth>
                <DateTimePicker
                  inputFormat="dd/MM/yyyy hh:mm"
                  value={elPackage.updatedAt}
                  onChange={(event) => {}}
                  renderInput={(params) => <TextField {...params} />}
                  disabled
                />
              </FormControl>
            </Grid>
          </>
        )}
        <Grid item xs={4} alignItems="center">
          {!re.test(location.pathname) && (
            <Grid container alignItems="center">
              <Controller
                name="hasDiagnostic"
                control={control}
                error={errors.hasDiagnostic ? true : false}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ pt: "5%" }}
                    control={<Checkbox {...field} checked={field.value} />}
                    disabled={
                      !["Admin", "Education Consultant"].includes(userRole)
                    }
                    label="Include Diagnostic Test?"
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
        <Grid item xs={8} />
        <Grid item xs={4}>
          {["Admin", "Education Consultant"].includes(userRole) && (
            <Button type="submit" variant="contained" startIcon={<SaveIcon />}>
              Save
            </Button>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default PackageUpdateDetails;
